import { Color } from "@repo/app-types/constants/colors";
import { defaultDateRange } from "@repo/app-types/entities/dateRange";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import type { TeammateAccountGrading } from "@repo/app-types/entities/teammatePerformanceSummary";
import {
  checkIsToolUsageAccuracyStatus,
  ToolUsageAccuracyStatus,
  type ToolUsageAccuracy,
} from "@repo/app-types/entities/toolUsageAccuracy";
import { SlideoverContainer } from "@repo/ui/Slideover/slideoverContainer";
import {
  SlideoverDialog,
  SlideoverDialogContentGray,
  SlideoverDialogTitleHeader,
} from "@repo/ui/Slideover/slideoverDialog";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import type { FC } from "react";
import { useCallback, useMemo, useState } from "react";
import {
  ToggleButton,
  ToggleButtonGroup,
  type Key,
} from "react-aria-components";
import { tv } from "tailwind-variants";
import { usePlaybookTeammateMostRecentGradingsInfiniteQuery } from "../../api/entities/performancePlaybook.js";
import { buildPlaybookScope } from "../filters/buildFilterQueryArgs.js";
import { ToolUsageAccuracyCallSlideoverDialogContainer } from "./toolUsageAccuracyCallSlideoverDialogContainer.js";
import { ToolUsageAccuracyGradingsTable } from "./toolUsageAccuracyGradingsTable.js";

interface ToolUsageAccuracySlideoverDialogProps {
  playbookId: PlaybookId;
  teammateId: TeammateId;
  toolId: string;
  toolName: string;
}

type ToolButtonColor =
  | typeof Color.Blue
  | typeof Color.Green
  | typeof Color.Gray
  | typeof Color.Red
  | typeof Color.Amber;

interface StatusBucket {
  status: ToolUsageAccuracyStatus | "all";
  label: string;
  color: ToolButtonColor;
  count: number;
  fraction: number;
}

interface GradingWithToolAccuracy extends TeammateAccountGrading {
  toolAccuracy: ToolUsageAccuracy | undefined;
}

const statusOrder: (ToolUsageAccuracyStatus | "all")[] = [
  "all",
  ToolUsageAccuracyStatus.UsedCorrect,
  ToolUsageAccuracyStatus.UnusedCorrect,
  ToolUsageAccuracyStatus.UsedIncorrect,
  ToolUsageAccuracyStatus.UsedFailed,
  ToolUsageAccuracyStatus.UnusedIncorrect,
];

const statusLabels: Record<ToolUsageAccuracyStatus | "all", string> = {
  all: "All",
  [ToolUsageAccuracyStatus.UsedCorrect]: "Used correctly",
  [ToolUsageAccuracyStatus.UnusedCorrect]: "N/A",
  [ToolUsageAccuracyStatus.UsedIncorrect]: "Used incorrectly",
  [ToolUsageAccuracyStatus.UsedFailed]: "Used with error",
  [ToolUsageAccuracyStatus.UnusedIncorrect]: "Failed to use",
};

const statusColors: Record<ToolUsageAccuracyStatus | "all", ToolButtonColor> = {
  all: Color.Blue,
  [ToolUsageAccuracyStatus.UsedCorrect]: Color.Green,
  [ToolUsageAccuracyStatus.UnusedCorrect]: Color.Gray,
  [ToolUsageAccuracyStatus.UsedIncorrect]: Color.Red,
  [ToolUsageAccuracyStatus.UsedFailed]: Color.Amber,
  [ToolUsageAccuracyStatus.UnusedIncorrect]: Color.Red,
};

const buttonStyle = tv({
  base: "-ml-[1px] flex flex-auto items-center justify-between gap-2 border border-border-light bg-white px-3 whitespace-nowrap text-gray-600 shadow-xs first-of-type:ml-0 first-of-type:rounded-l-lg last-of-type:rounded-r-lg hover:bg-gray-50 data-[implicitly-selected]:z-10",
  variants: {
    /* prettier-ignore */
    color: {
      [Color.Red]: "data-[implicitly-selected]:border-red-300 data-[implicitly-selected]:bg-red-300 data-[implicitly-selected]:hover:bg-red-300 data-[implicitly-selected]:text-red-900",
      [Color.Orange]: "data-[implicitly-selected]:border-orange-300 data-[implicitly-selected]:bg-orange-300 data-[implicitly-selected]:hover:bg-orange-300 data-[implicitly-selected]:text-orange-900",
      [Color.Amber]: "data-[implicitly-selected]:border-amber-300 data-[implicitly-selected]:bg-amber-300 data-[implicitly-selected]:hover:bg-amber-300 data-[implicitly-selected]:text-amber-900",
      [Color.Yellow]: "data-[implicitly-selected]:border-yellow-300 data-[implicitly-selected]:bg-yellow-300 data-[implicitly-selected]:hover:bg-yellow-300 data-[implicitly-selected]:text-yellow-900",
      [Color.Lime]: "data-[implicitly-selected]:border-lime-300 data-[implicitly-selected]:bg-lime-300 data-[implicitly-selected]:hover:bg-lime-300 data-[implicitly-selected]:text-lime-900",
      [Color.Green]: "data-[implicitly-selected]:border-green-300 data-[implicitly-selected]:bg-green-300 data-[implicitly-selected]:hover:bg-green-300 data-[implicitly-selected]:text-green-900",
      [Color.Emerald]: "data-[implicitly-selected]:border-emerald-300 data-[implicitly-selected]:bg-emerald-300 data-[implicitly-selected]:hover:bg-emerald-300 data-[implicitly-selected]:text-emerald-900",
      [Color.DarkEmerald]: "data-[implicitly-selected]:border-emerald-500 data-[implicitly-selected]:bg-emerald-500 data-[implicitly-selected]:hover:bg-emerald-500 data-[implicitly-selected]:text-emerald-900",
      [Color.Blue]: "data-[implicitly-selected]:border-blue-300 data-[implicitly-selected]:bg-blue-300 data-[implicitly-selected]:hover:bg-blue-300 data-[implicitly-selected]:text-blue-900",
      [Color.Gray]: "data-[implicitly-selected]:border-border-strong data-[implicitly-selected]:bg-gray-300 data-[implicitly-selected]:hover:bg-gray-300 data-[implicitly-selected]:text-foreground",
    },
  },
});

export const ToolUsageAccuracySlideoverDialog: FC<
  ToolUsageAccuracySlideoverDialogProps
> = (props) => {
  const { playbookId, teammateId, toolId, toolName } = props;

  const [filterStatus, setFilterStatus] = useState<
    ToolUsageAccuracyStatus | "all"
  >("all");

  const now = useNow();

  // Fetch all gradings
  const {
    data: gradingsData,
    isLoading: isLoadingGradings,
    isError: isErrorGradings,
    hasNextPage: canFetchMoreGradings,
    fetchNextPage: fetchMoreGradings,
  } = usePlaybookTeammateMostRecentGradingsInfiniteQuery({
    playbookId,
    teammateId,
    scope: buildPlaybookScope(now, playbookId, defaultDateRange),
    filters: [],
  });

  // Flatten all pages of gradings
  const allGradings = useMemo(() => {
    return gradingsData?.pages.flatMap((page) => page.recentGradings);
  }, [gradingsData]);

  // Stop paginating when we encounter a page without gradings having tool usage accuracy for this tool. This is probably a new tool.
  const canFetchMoreGradingsWithToolUsageAccuracy = useMemo(() => {
    const lastPageGradings =
      gradingsData?.pages[gradingsData.pages.length - 1]?.recentGradings;

    return (
      canFetchMoreGradings &&
      lastPageGradings !== undefined &&
      lastPageGradings?.some(
        (grading) => grading.tool_usage_accuracy?.tools[toolId] !== undefined,
      )
    );
  }, [gradingsData, toolId, canFetchMoreGradings]);

  // Extract gradings with tool accuracy for this specific tool
  const gradingsWithToolAccuracy =
    useMemo((): readonly GradingWithToolAccuracy[] => {
      if (!allGradings) {
        return [];
      }

      return allGradings
        .map((grading) => {
          const toolAccuracy = grading.tool_usage_accuracy?.tools[toolId];
          return {
            ...grading,
            toolAccuracy,
          };
        })
        .filter((grading) => grading.toolAccuracy !== undefined);
    }, [allGradings, toolId]);

  // Build distribution buckets
  const buckets = useMemo((): StatusBucket[] => {
    const counts = new Map<ToolUsageAccuracyStatus | "all", number>();

    // Initialize counts
    for (const status of statusOrder) {
      counts.set(status, 0);
    }

    // Count by status
    for (const grading of gradingsWithToolAccuracy) {
      if (grading.toolAccuracy) {
        const currentCount = counts.get(grading.toolAccuracy.status) ?? 0;
        counts.set(grading.toolAccuracy.status, currentCount + 1);
      }
    }

    // Set "all" count
    counts.set("all", gradingsWithToolAccuracy.length);

    const totalCount = gradingsWithToolAccuracy.length;

    return statusOrder
      .filter((status) => {
        // Always show "all" if there are any gradings
        if (status === "all") {
          return totalCount > 0;
        }
        // Only show statuses with counts > 0
        return (counts.get(status) ?? 0) > 0;
      })
      .map((status) => {
        const count = counts.get(status) ?? 0;
        return {
          status,
          label: statusLabels[status],
          color: statusColors[status],
          count,
          fraction: totalCount > 0 ? count / totalCount : 0,
        };
      });
  }, [gradingsWithToolAccuracy]);

  // Filter gradings based on selected status
  const filteredGradings = useMemo(() => {
    if (filterStatus === "all") {
      return gradingsWithToolAccuracy;
    }

    return gradingsWithToolAccuracy.filter(
      (grading) => grading.toolAccuracy?.status === filterStatus,
    );
  }, [filterStatus, gradingsWithToolAccuracy]);

  const selectedKeys = useMemo(() => [filterStatus], [filterStatus]);

  const onSelectionChange = useCallback((keys: Set<Key>) => {
    const key = keys.values().next().value;

    if (
      typeof key === "string" &&
      (key === "all" || checkIsToolUsageAccuracyStatus(key))
    ) {
      setFilterStatus(key);
    }
  }, []);

  // Nested slideover state for call details
  const [openedGrading, setOpenedGrading] = useState<
    GradingWithToolAccuracy | undefined
  >(undefined);

  const handleGradingClick = (grading: GradingWithToolAccuracy): void => {
    setOpenedGrading(grading);
  };

  const handleCallSlideoverOpenChange = (newIsOpen: boolean): void => {
    if (!newIsOpen) {
      setOpenedGrading(undefined);
    }
  };

  return (
    <SlideoverDialog label={`${toolName} tool accuracy`}>
      <SlideoverDialogTitleHeader isLoading={false}>
        {toolName}
      </SlideoverDialogTitleHeader>
      <SlideoverDialogContentGray>
        <div className="flex flex-col gap-5 px-4 py-4">
          {/* Distribution bar */}
          {buckets.length > 0 ? (
            <div>
              <ToggleButtonGroup
                className="flex h-8 w-full items-stretch justify-stretch"
                selectionMode="single"
                selectedKeys={selectedKeys}
                onSelectionChange={onSelectionChange}
                disallowEmptySelection
              >
                {buckets.map((bucket) => (
                  <ToggleButton
                    key={bucket.status}
                    id={bucket.status}
                    className={buttonStyle({ color: bucket.color })}
                    data-implicitly-selected={
                      filterStatus === bucket.status ? "true" : undefined
                    }
                    style={{
                      flexBasis:
                        bucket.status === "all"
                          ? undefined
                          : `${(bucket.fraction * 100).toFixed(1)}%`,
                      flexGrow: bucket.status === "all" ? 0 : 1,
                    }}
                  >
                    <span className="text-sm font-semibold">
                      {bucket.label}
                    </span>
                    <span className="text-xs">
                      {bucket.count}
                      {bucket.status !== "all" && isFinite(bucket.fraction)
                        ? ` (${(bucket.fraction * 100).toFixed(0)}%)`
                        : null}
                    </span>
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </div>
          ) : null}

          {/* Gradings table */}
          <div>
            <ToolUsageAccuracyGradingsTable
              gradings={filteredGradings}
              unfilteredEntityCount={allGradings?.length}
              isLoading={isLoadingGradings}
              isError={isErrorGradings}
              onLoadMore={
                canFetchMoreGradingsWithToolUsageAccuracy
                  ? fetchMoreGradings
                  : undefined
              }
              onGradingAction={handleGradingClick}
            />
          </div>
        </div>
      </SlideoverDialogContentGray>

      {/* Nested slideover for call details */}
      <SlideoverContainer
        isDismissable
        isOpen={openedGrading !== undefined}
        onOpenChange={handleCallSlideoverOpenChange}
      >
        {openedGrading ? (
          <ToolUsageAccuracyCallSlideoverDialogContainer
            gradingId={openedGrading.id}
            toolId={toolId}
            toolName={toolName}
          />
        ) : null}
      </SlideoverContainer>
    </SlideoverDialog>
  );
};
